import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { AbstractControl, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom, Observable } from 'rxjs';

import { IOPayload } from '@connect-services/client/models/notification.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { SESSION_STORE_KEY } from '@connect-services/client/resolvers/session.resolver';
import {
    Me,
    PasswordRules,
    SecurityControllerLogin200Response,
    SecurityService as ApiSecurityService,
    SessionService,
} from '@connect-services/client/sdk/api';
import { BlocManagerService } from '@connect-services/client/services/bloc-manager.service';
import { BroadcastChannelService } from '@connect-services/client/services/broadcast-channel.service';
import { MapSearchService } from '@connect-services/client/services/map-search.service';
import { NotificationService } from '@connect-services/client/services/notification.service';
import { PanelService } from '@connect-services/client/services/panel.service';
import { ProjectSessionStorageService } from '@connect-services/client/services/project-session-storage.service';
import { ValidatorsService } from '@connect-services/client/services/validators.service';
import {
    ConfirmedPasswordFormGroup,
    ConfirmedPasswordFormType,
    SecurityFormField,
} from '@shared/client/models/form.model';
import { AbstractSecurityService } from '@shared/client/services/security.service';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

@Injectable({
    providedIn: 'root',
})
export class SecurityService extends AbstractSecurityService<Me> {
    public readonly initialized: WritableSignal<boolean> = signal(false);
    public readonly meSignal: WritableSignal<Me | null> = signal(null);

    private readonly apiSecurityService = inject(ApiSecurityService);
    private readonly blocsManagerService = inject(BlocManagerService);
    private readonly broadcastChannelService = inject(BroadcastChannelService);
    private readonly mapSearchService = inject(MapSearchService);
    private readonly notificationService = inject(NotificationService<IOPayload>);
    private readonly panelService = inject(PanelService);
    private readonly projectSessionStorageService = inject(ProjectSessionStorageService);
    private readonly router = inject(Router);
    private readonly sessionService = inject(SessionService);
    private readonly snackBarService = inject(SnackBarService);

    public async changePassword(password: string, token: string): Promise<void> {
        try {
            await firstValueFrom(
                this.apiSecurityService.securityControllerUpdatePassword({
                    authorization: `Bearer ${token}`,
                    updatePasswordDto: { password: password },
                }),
            );
            this.snackBarService.openSuccessSnackBar('changePasswordSuccess');
        } catch {
            this.snackBarService.openErrorSnackBar('changePasswordError');
        } finally {
            await this.router.navigate(['/', RoutePathEnum.LOGIN]);
        }
    }

    public async finalizeInvitation(password: string, token: string): Promise<void> {
        try {
            await firstValueFrom(
                this.apiSecurityService.securityControllerFinalizeInvitation({
                    addPasswordDto: { password: password },
                    xInvitationToken: token,
                }),
            );
            this.snackBarService.openSuccessSnackBar('finalizeInvitationSuccess');
        } catch {
            this.snackBarService.openErrorSnackBar('finalizeInvitationError');
        } finally {
            await this.router.navigate(['/', RoutePathEnum.LOGIN]);
        }
    }

    public async forgotPassword(login: string): Promise<void> {
        await firstValueFrom(
            this.apiSecurityService.securityControllerRequestResetPassword({
                requestResetPasswordDto: {
                    changePasswordPath: `/${RoutePathEnum.CHANGE_PASSWORD}`,
                    login: login,
                },
            }),
        );
        this.snackBarService.openSuccessSnackBar('forgotPasswordRequestSentSuccess');
        await this.router.navigate(['/', RoutePathEnum.LOGIN]);
    }

    public getConfirmedPasswordForm(rules: PasswordRules | null): ConfirmedPasswordFormGroup {
        return new ConfirmedPasswordFormGroup(
            {
                [SecurityFormField.CONFIRM_PASSWORD]: new FormControl<string>('', {
                    nonNullable: true,
                    validators: [Validators.required],
                }),
                [SecurityFormField.PASSWORD]: new FormControl<string>('', {
                    nonNullable: true,
                    validators: [
                        ...ValidatorsService.stringValidationForm({ minLength: rules?.minLength ?? 0 }),
                        ValidatorsService.isValidPassword(rules),
                    ],
                }),
            },
            {
                validators: (control: AbstractControl<ConfirmedPasswordFormType>) =>
                    ValidatorsService.valuesEqual(control, [
                        SecurityFormField.CONFIRM_PASSWORD,
                        SecurityFormField.PASSWORD,
                    ]),
            },
        );
    }

    public getMe(token?: string): Promise<Me> {
        return firstValueFrom(
            this.apiSecurityService.securityControllerGetMe({
                ...(token && { authorization: `Bearer ${token}` }),
            }),
        );
    }

    public override async initSession(): Promise<void> {
        await super.initSession();
        this.initialized.set(true);

        return;
    }

    public login(login: string, password: string): Promise<void> {
        return this.manageLoginResponse(
            this.apiSecurityService.securityControllerLogin({
                loginDto: { login: login, password: password },
            }),
        );
    }

    /**
     * Logout the user
     * @param [redirectToLogin = true] {boolean} - Redirect to login page after logout.
     * @param [endSession = true] {boolean}
     * @description **Caution**: If this parameter is set to false, make sure to be on a security component page.
     */
    public async logout(redirectToLogin: boolean = true, endSession: boolean = true): Promise<boolean> {
        await this.setSession(null);
        this.notificationService.disconnect();

        this.broadcastChannelService.closeAllExternalWindows();

        const sessionId = sessionStorage.getItem(SESSION_STORE_KEY);

        if (sessionId && endSession) {
            try {
                await firstValueFrom(this.sessionService.sessionControllerPatch({ id: sessionId }));
            } catch {
                //We do nothing here because we want to log out anyway
            }
        }

        if (redirectToLogin) {
            await this.router.navigate(['/', RoutePathEnum.LOGIN]);
        }

        this.blocsManagerService.resetBlocs();

        // Clearing panel content to avoid reoponing when changing account
        this.panelService.closePanel();
        // Avoid map centering to nothing when chaging account
        this.mapSearchService.selectedSearchItem.set(undefined);

        this.projectSessionStorageService.clear();

        // clear all data in local storage to be sure that data isn't preserve in local storage when user logout
        localStorage.clear();

        return true;
    }

    public oneGeoSuitelogin(token: string, topic: string): Promise<void> {
        return this.manageLoginResponse(
            this.apiSecurityService.securityControllerOneGeoSuiteLogin({
                body: { token, topic },
            }),
        );
    }

    private async manageLoginResponse(loginObservable: Observable<SecurityControllerLogin200Response>): Promise<void> {
        try {
            const response = await firstValueFrom(loginObservable);
            await this.setSession(response.access_token);
            this.notificationService.connect();
            await this.router.navigate(['/']);
        } catch (error: unknown) {
            if (
                error instanceof HttpErrorResponse &&
                error.status === HttpStatusCode.Unauthorized &&
                error.error?.message &&
                error.error.error
            ) {
                this.snackBarService.openErrorSnackBar(error.error.message);

                return;
            }

            this.snackBarService.openErrorSnackBar('loginError');
        }
    }
}
